import {
  PageHeader,
  PageTitle,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  titleCase,
} from '@westhetics/ui'
import { useNavigate, useParams } from 'react-router'
import { Page } from '@/app/shell'
import { roleLabel, useSession, type Profile } from '@/lib/session'
import { routes } from '@/routes'
import { useConnections } from './api'
import { ConnectionsTab } from './connections-tab'
import { ACCOUNT_TABS, TAB_LABELS, type AccountTab } from './labels'
import { PasswordTab } from './password-tab'
import { SessionsTab } from './sessions-tab'

/**
 * A conta da própria pessoa (docs/areas/autenticacao.md, "Conta"; P-AUT-25). Arquétipo: ficha com
 * abas: Acessos, Aplicativos conectados e Senha, cada uma com um assunto só. Fora da administração
 * e aberta a todo perfil, inclusive root em visão global. Sem ação primária no cabeçalho: a única
 * é "Salvar nova senha", dentro da aba Senha. A entrada é o nome no rodapé da sidebar.
 */
export function AccountPage() {
  const { tab } = useParams()
  const navigate = useNavigate()
  const session = useSession()
  const connections = useConnections()
  const profile = session.profile

  const active: AccountTab = ACCOUNT_TABS.includes(tab as AccountTab)
    ? (tab as AccountTab)
    : 'sessions'
  const connectionCount = connections.data?.length ?? 0

  return (
    <>
      <PageHeader crumbs={[{ label: 'Sua conta' }]} />
      <Page wide>
        <PageTitle
          title={profile ? titleCase(profile.name) : 'Sua conta'}
          meta={profile ? identification(profile) : undefined}
        />
        <Tabs
          className="mt-6"
          value={active}
          onValueChange={(value) => navigate(routes.accountTab(value as AccountTab))}
        >
          <TabsList>
            <TabsTrigger value="sessions">{TAB_LABELS.sessions}</TabsTrigger>
            <TabsTrigger value="connections">
              {TAB_LABELS.connections}
              {connectionCount ? ` ${connectionCount}` : ''}
            </TabsTrigger>
            <TabsTrigger value="password">{TAB_LABELS.password}</TabsTrigger>
          </TabsList>
          <TabsContent value="sessions">
            <SessionsTab />
          </TabsContent>
          <TabsContent value="connections">
            <ConnectionsTab />
          </TabsContent>
          <TabsContent value="password">
            <PasswordTab />
          </TabsContent>
        </Tabs>
      </Page>
    </>
  )
}

/**
 * "gabriel@clinica.com.br · Gestora na Westhetics Experience"; root: "Westhetics · na Clínica
 * Aurora" ou "Westhetics · visão global".
 */
function identification(profile: Profile) {
  const clinic = profile.activeClinic
  const where = profile.isRoot
    ? `Westhetics · ${clinic ? `na ${clinic.name}` : 'visão global'}`
    : clinic
      ? `${roleLabel(clinic.role)} na ${clinic.name}`
      : roleLabel(profile.role)
  return `${profile.email} · ${where}`
}
